import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { useLocation } from "react-router";

/** Hover opens and closes with a short delay, so crossing from the bar to the panel doesn't close it. */
const HOVER_DELAY_MS = 150;

/**
 * Menu items that each open a panel (the site's menu, the admin's): one open at a time, by hover
 * (mouse, wide screens only) or click. A panel opened by hovering stays open on the click that
 * usually follows; Escape (focus back on its button), a click outside or navigating closes it.
 * `hoverQuery`: the media query where hovering opens (below it, items only unfold on a tap).
 */
export function useDropdowns<T extends string>(hoverQuery: string) {
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState<T | null>(null);
  const openedBy = useRef<"hover" | "click">("click");
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const root = useRef<HTMLDivElement>(null);
  const buttons = useRef<Partial<Record<T, HTMLButtonElement | null>>>({});

  const show = (id: T | null, by: "hover" | "click") => {
    clearTimeout(timer.current);
    openedBy.current = by;
    setOpen(id);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => setOpen(null), [pathname, search]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(null);
      buttons.current[open]?.focus();
    };
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open]);

  const hover = (id: T | null) => (e: ReactPointerEvent) => {
    // Touch has no hover; in a narrow layout (a drawer, even with a mouse) hovering down the list
    // would open and shut items under the cursor.
    if (e.pointerType !== "mouse" || !(window.matchMedia?.(hoverQuery).matches ?? true)) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => show(id, "hover"), HOVER_DELAY_MS);
  };

  return {
    open,
    close: () => setOpen(null),
    rootRef: root,
    /** On the wrapper around an item's button and its panel. */
    itemProps: (id: T) => ({ onPointerEnter: hover(id), onPointerLeave: hover(null) }),
    buttonProps: (id: T) => ({
      ref: (el: HTMLButtonElement | null) => {
        buttons.current[id] = el;
      },
      "aria-expanded": open === id,
      "aria-controls": `dropdown-${id}`,
      onClick: () => {
        if (open === id && openedBy.current === "hover") openedBy.current = "click";
        else show(open === id ? null : id, "click");
      },
    }),
    panelId: (id: T) => `dropdown-${id}`,
  };
}
